import React from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import { useTranslation } from 'react-i18next';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface OrderStatusChartProps {
  data: {
    [key: string]: number;
  };
}

// Color palette for different statuses
const STATUS_COLORS = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#14b8a6', '#f97316', '#6366f1',
  '#84cc16', '#0ea5e9', '#d946ef', '#f43f5e', '#22c55e',
];

// Function to translate order status using existing translations
const getStatusLabel = (status: string, t: any): string => {
  // Try to get translation from orders.statusOptions first
  const translation = t(`orders:statusOptions.${status}`);

  // If translation exists and is not the same as the key, use it
  if (translation && translation !== `orders:statusOptions.${status}`) {
    return translation;
  }

  // Fallback to shipforme translations
  const shipForMeTranslation = t(`shipforme:statusOptions.${status}`);
  if (shipForMeTranslation && shipForMeTranslation !== `shipforme:statusOptions.${status}`) {
    return shipForMeTranslation;
  }

  // Final fallback
  return `Status ${status}`;
};

export const OrderStatusChart: React.FC<OrderStatusChartProps> = ({ data }) => {
  const { t } = useTranslation();

  const statusLabels = Object.keys(data);
  const statusValues = Object.values(data);

  const chartData = {
    labels: statusLabels.map(status => getStatusLabel(status, t)),
    datasets: [
      {
        label: t('common.orders'),
        data: statusValues,
        backgroundColor: statusLabels.map((_, index) => STATUS_COLORS[index % STATUS_COLORS.length]),
        borderColor: statusLabels.map((_, index) => STATUS_COLORS[index % STATUS_COLORS.length]),
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    indexAxis: 'y' as const,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
      },
    },
  };

  const hasData = statusValues.length > 0 && statusValues.some(v => v > 0);

  return (
    <div className="chart-container">
      <h3>{t('dashboard:orderStatus')}</h3>
      {hasData ? (
        <Bar data={chartData} options={options} />
      ) : (
        <div className="no-data-message">
          <p>{t('dashboard:noData')}</p>
        </div>
      )}
    </div>
  );
};

